import axiosInstance from './axiosInstance';
import { IBrandGuideSection } from '../types/damTypes';

export interface CreateBrandGuideSectionDto {
  title: string;
  slug: string;
  icon?: string;
  previewText: string;
  introText?: string;
  contentHtml?: string;
  images?: string[];
  displayOrder?: number;
  isPublished?: boolean;
  // Kortdesign
  cardGradientStart?: string;
  cardGradientEnd?: string;
  cardSymbol?: string;
  cardImage?: string;
}

export interface UpdateBrandGuideSectionDto {
  title?: string;
  newSlug?: string;
  icon?: string;
  previewText?: string;
  introText?: string;
  contentHtml?: string;
  images?: string[];
  displayOrder?: number;
  isPublished?: boolean;
  // Kortdesign
  cardGradientStart?: string;
  cardGradientEnd?: string;
  cardSymbol?: string;
  cardImage?: string;
}

export const brandGuideService = {
  async fetchSections(publishedOnly = true) {
    const { data } = await axiosInstance.get<IBrandGuideSection[]>(
      '/api/brand-guide/sections',
      {
        params: { publishedOnly },
      }
    );
    return data;
  },

  async fetchSection(slug: string) {
    const { data } = await axiosInstance.get<IBrandGuideSection>(
      `/api/brand-guide/sections/${slug}`
    );
    return data;
  },

  async createSection(payload: CreateBrandGuideSectionDto) {
    const { data } = await axiosInstance.post<IBrandGuideSection>(
      '/api/brand-guide/sections',
      payload
    );
    return data;
  },

  async updateSection(slug: string, payload: UpdateBrandGuideSectionDto) {
    const { data } = await axiosInstance.put<IBrandGuideSection>(
      `/api/brand-guide/sections/${slug}`,
      payload
    );
    return data;
  },

  async deleteSection(slug: string) {
    await axiosInstance.delete(`/api/brand-guide/sections/${slug}`);
  },

  async uploadImage(slug: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    const { data } = await axiosInstance.post<{ imageUrl: string }>(
      `/api/brand-guide/sections/${slug}/images`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data.imageUrl;
  },

  // PDF-hantering
  async getPdfInfo() {
    const { data } = await axiosInstance.get<{
      exists: boolean;
      url?: string;
      size?: number;
      updatedAt?: string;
    }>('/api/brand-guide/pdf');
    return data;
  },

  async uploadPdf(file: File) {
    const formData = new FormData();
    formData.append('pdf', file);
    const { data } = await axiosInstance.post<{
      message: string;
      url: string;
      size: number;
      updatedAt: string;
    }>('/api/brand-guide/pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async deletePdf() {
    await axiosInstance.delete('/api/brand-guide/pdf');
  },

  // Inställningar
  async getSettings() {
    const { data } = await axiosInstance.get<{
      headerSubtitle: string;
      headerTitle: string;
      headerDescription: string;
      footerText: string;
    }>('/api/brand-guide/settings');
    return data;
  },

  async updateSettings(settings: {
    headerSubtitle?: string;
    headerTitle?: string;
    headerDescription?: string;
    footerText?: string;
  }) {
    const { data } = await axiosInstance.put('/api/brand-guide/settings', settings);
    return data;
  },

  // Export/Import
  async exportData(): Promise<Blob> {
    const response = await axiosInstance.post('/api/brand-guide/export', {}, {
      responseType: 'blob'
    });
    return response.data;
  },

  async importData(file: File): Promise<{ sectionsImported: number; filesImported: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axiosInstance.post('/api/brand-guide/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data;
  },
};

export default brandGuideService;

